import {
  BrandMediaInfo,
  ImageSize,
  ImageSizes,
  MediaContentType,
  MediaUrlOptions,
  PropertyMediaInfo,
} from './image.models';
import { defaultImagePlaceholder, imageTypes, MediaType, VIDEO_TYPES } from './image.const';

/**
 * Helper function that analyses if a media info object, has a valid status to
 * be able to get published online.
 *
 * @param recordMediaInfoArray Record to be analysed
 * @return This dictates if the media is publishable or not
 */
export function isMediaInfoPublishable(recordMediaInfoArray: PropertyMediaInfo): boolean {
  return (
    recordMediaInfoArray == null ||
    !(recordMediaInfoArray.watermark_label === 'CostarWaterMark' || recordMediaInfoArray.publish_image_f === false)
  );
}

export function extractFileExtension(media_path: string): string {
  const splitValues = media_path.split('.');
  return splitValues[splitValues.length - 1];
}

export function isMediaTypeImage(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  return mediaRecord.media_type_desc === MediaType.Image;
}

export function isMediaTypeVideo(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  return mediaRecord.media_type_desc === MediaType.Video;
}

export function isMediaExtensionImage(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  const mediaExtension = extractFileExtension(mediaRecord.media_path);
  return imageTypes.some(type => type === mediaExtension.toLowerCase());
}

export function isMediaExtensionVideo(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  const mediaExtension = this.extractFileExtension(mediaRecord.media_path);
  return VIDEO_TYPES.some(type => type === '.' + mediaExtension.toLowerCase());
}

/**
 * Review is media record type is Image & it's extension coincides with image
 * extension
 */
export function isMediaImage(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  return isMediaTypeImage(mediaRecord) && isMediaExtensionImage(mediaRecord);
}

/**
 * Review is media record type is Video & it's extension coincides with video
 * extension
 */
export function isMediaVideo(mediaRecord: PropertyMediaInfo | BrandMediaInfo) {
  return isMediaTypeVideo(mediaRecord) && isMediaExtensionVideo(mediaRecord);
}
/**
 * This function formats a passed url with a proportioned prefix. It uses a url
 * splitter string to split the url into pieces so it can use the later section
 * of the split url and append the provided prefix.
 *
 * @example
 * ```
 * mediaUrlFormatter('prop', 'miq.com/aws/', '/prop/3/534/image.jpg')
 * // result = 'miq.com/aws/3/534/image.jpg'
 * mediaUrlFormatter('prop', 'miq.com/aws/', 'aws.com/prop/3/534/image.jpg')
 * // result = 'miq.com/aws/3/534/image.jpg'
 *
 * @returns urlPrefix + splitValueWithoutFirstSlash
 * ```
 */
function mediaUrlFormatter(urlSplitter: string, urlPrefix: string, url: string): string {
  let splitValues = url.split(urlSplitter);
  const urlValue = splitValues[1] === '/' ? splitValues[2] : splitValues[1];
  return `${urlPrefix}${urlValue.substr(1)}`;
}

/**
 * Wrap {@link mediaUrlFormatter} that returns a default image placeholder if
 * flag is on.
 *
 * Apply some validations before formatting url.
 *
 * @see mediaUrlFormatter
 */
function formatImageUrl(urlSplitter: string, urlPrefix: string, url: string, provideDefault = true): string {
  let imageUrl = provideDefault ? defaultImagePlaceholder : null;
  if (url != null && url.includes(urlSplitter)) {
    imageUrl = mediaUrlFormatter(urlSplitter, urlPrefix, url);
  }
  return imageUrl;
}

/**
 * Generate a URL string using the provided original URL. Internally, it uses
 * {@link formatImageUrl} to format the expected URL.
 * @param splitter
 * @param prefix
 * @param url
 * @param options
 */
export function genOriginalMediaURI(
  splitter: string,
  prefix: string,
  url: string,
  options: { provideDefault?: boolean } = {}
) {
  const { provideDefault = true } = options;
  return formatImageUrl(splitter, prefix, url, provideDefault);
}

/**
 * Generate the thumbnail URL from the input URL. Internally, it uses
 * {@link formatImageUrl} to generate and format the URL. The resulting URI
 * will contain the prefix followed by the requested image size (**defaulted
 * to medium**).
 *
 * prefix + imageSize + '/'
 *
 * @see ImageSize
 * @see ImageSizes
 */
export function genThumbMediaURI(
  splitter: string,
  prefix: string,
  url: string,
  options: MediaUrlOptions & { provideDefault?: boolean } = {}
): string {
  const { provideDefault = true, imgSize = ImageSize.Medium } = options;
  return formatImageUrl(splitter, `${prefix}${ImageSizes[imgSize]}/`, url, provideDefault);
}

/**
 * Wrapper function for {@link genThumbMediaURI} using environment values to
 * generated the required thumbnail URL.
 *
 * @param url
 * @param options
 */
export function genThumbnailUrl(url: string, options: MediaUrlOptions & { provideDefault?: boolean } = {}): string {
  const media = {
    // prop-images required to work with  proxy.conf.js
    originalLocationURI: '/mprop-images/',
    thumbLocationURI: '/prop-images/thumb/',
    edpMultimediaKey: 'adv-multimedia-np',
  };
  const { edpMultimediaKey, thumbLocationURI } = media;
  return genThumbMediaURI(edpMultimediaKey, thumbLocationURI, url, options);
}

export function getPrimaryImageOrFirst(
  recordMediaArray: Array<PropertyMediaInfo | BrandMediaInfo>
): PropertyMediaInfo | BrandMediaInfo {
  let image = getPrimaryImage(recordMediaArray);
  if (!image && recordMediaArray != null && recordMediaArray.length) {
    const sortedImages = ((Array.isArray(recordMediaArray) && recordMediaArray) || [])
      .filter(isMediaImage)
      .sort((imageA, imageB) => imageB.id - imageA.id);
    image = sortedImages.length > 0 ? sortedImages[0] : null;
  }
  return image;
}

export function getImageByType(
  recordMediaArray: Array<PropertyMediaInfo | BrandMediaInfo>
): PropertyMediaInfo | BrandMediaInfo {
  const sortedImages = recordMediaArray.slice().sort((a, b) => b.id - a.id);
  const imageByType = sortedImages.find(recordMedia => {
    const { media_content_type_desc } = recordMedia as PropertyMediaInfo;
    return media_content_type_desc === MediaContentType.FloorPlan;
  });
  return imageByType;
}

export function getPrimaryImage(mediaRecords: Array<PropertyMediaInfo | BrandMediaInfo>) {
  return (mediaRecords || [])
    .slice()
    .sort((a, b) => b.id - a.id)
    .find(recordMedia => recordMedia.primary_image_f && isMediaImage(recordMedia));
}
